import { CABINET_PATHS, CABINET_PHOTO_SOURCE } from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { combineLatest, firstValueFrom, map, startWith, Subject } from 'rxjs';
import { UserAdminService } from '@nebalkon-workspace/admin-api';
import { PhotoUploadService } from '@nebalkon-workspace/media-api';
import { CabinetButtonComponent } from '@nebalkon-workspace/cabinet-client';
import {
  NotInput,
  NotPhoneInput,
  NotSystemNotificationService,
} from '@nebalkon-workspace/ui-kit';
import { getErrorText, mobilePhoneValidator } from '@nebalkon-workspace/utils';

const FIELD_LIMITS = {
  NAME_MAX: 30,
  MIN_LENGTH: 2,
} as const;

@Component({
  selector: 'lib-staff-client-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    NotInput,
    NotPhoneInput,
    CabinetButtonComponent,
  ],
  templateUrl: './staff-client-form.component.html',
  styleUrl: './staff-client-form.component.scss',
})
export class StaffClientFormComponent {
  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #photoSource = inject(CABINET_PHOTO_SOURCE, { optional: true });
  readonly #fb = inject(FormBuilder);
  readonly #userAdmin = inject(UserAdminService);
  readonly #photoUpload = inject(PhotoUploadService);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #translate = inject(TranslateService);

  protected readonly form: FormGroup;
  protected readonly isSubmitting = signal(false);
  protected readonly unfocusedPhoneInput = new Subject<boolean>();
  protected readonly phoneControlErrorText;

  protected readonly photoFile = signal<File | null>(null);
  protected readonly photoPreview = signal<string | null>(null);

  protected readonly displayPhotoUrl = computed(() => this.photoPreview());

  protected readonly isSubmitDisabled = computed(
    () => this.isSubmitting(),
  );

  constructor() {
    this.form = this.#fb.group({
      phone: ['', [Validators.required, mobilePhoneValidator()]],
      lastName: [
        '',
        [
          Validators.required,
          Validators.minLength(FIELD_LIMITS.MIN_LENGTH),
          Validators.maxLength(FIELD_LIMITS.NAME_MAX),
        ],
      ],
      firstName: [
        '',
        [
          Validators.required,
          Validators.minLength(FIELD_LIMITS.MIN_LENGTH),
          Validators.maxLength(FIELD_LIMITS.NAME_MAX),
        ],
      ],
      patronymic: [
        '',
        [
          Validators.required,
          Validators.minLength(FIELD_LIMITS.MIN_LENGTH),
          Validators.maxLength(FIELD_LIMITS.NAME_MAX),
        ],
      ],
      email: ['', [Validators.email]],
      telegram_link: [''],
      whatsapp_link: [''],
      viber_link: [''],
    });

    const prefillPhone = this.#route.snapshot.queryParamMap.get('phone')?.trim();
    if (prefillPhone) {
      this.form.patchValue({ phone: prefillPhone });
    }

    this.phoneControlErrorText = toSignal(
      combineLatest([
        this.form.controls['phone'].valueChanges.pipe(startWith('')),
        this.unfocusedPhoneInput,
      ]).pipe(
        map(() =>
          getErrorText(this.form.controls['phone'], {
            required: this.#translate.instant(
              'admin.users.create.errors.required',
            ),
            invalidPhone: this.#translate.instant(
              'admin.users.create.errors.invalidOperator',
            ),
            landlinePhone: this.#translate.instant(
              'admin.users.create.errors.landlinePhone',
            ),
            invalidOperatorCode: this.#translate.instant(
              'admin.users.create.errors.invalidOperator',
            ),
          }),
        ),
      ),
      { initialValue: '' },
    );
  }

  protected getFieldError(fieldName: string): string {
    const control = this.form.get(fieldName);
    if (!control || !control.touched || !control.errors) {
      return '';
    }

    const errors = control.errors;

    if (errors['required']) {
      return 'admin.users.create.errors.required';
    }
    if (errors['minlength']) {
      return 'admin.users.create.errors.minLength';
    }
    if (errors['maxlength']) {
      const max = errors['maxlength'].requiredLength;
      return this.#translate.instant('admin.users.create.errors.maxLength', {
        max,
      });
    }
    if (errors['email']) {
      return 'admin.users.create.errors.invalidEmail';
    }

    return '';
  }

  protected async onPickPhoto(): Promise<void> {
    if (!this.#photoSource) {
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.users.create.photoUploadError')],
      });
      return;
    }

    const files = await this.#photoSource.pickPhotos({
      maxCount: 1,
      accept: 'image/jpeg,image/png,image/webp',
    });
    const file = files[0];
    if (!file) {
      return;
    }

    const preview = URL.createObjectURL(file);
    this.photoFile.set(file);
    this.photoPreview.set(preview);
  }

  protected clearPendingPhoto(): void {
    const preview = this.photoPreview();
    if (preview?.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }

    this.photoFile.set(null);
    this.photoPreview.set(null);
  }

  protected async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key)?.markAsTouched();
      });
      this.unfocusedPhoneInput.next(true);
      return;
    }

    this.isSubmitting.set(true);

    try {
      const formValue = this.form.value;
      const phone = (formValue.phone ?? '').trim();

      const created = await firstValueFrom(
        this.#userAdmin.createClient({
          phone,
          first_name: formValue.firstName.trim(),
          last_name: formValue.lastName.trim(),
          patronymic: formValue.patronymic.trim(),
          email: formValue.email?.trim() || undefined,
          telegram_link: formValue.telegram_link?.trim() || undefined,
          whatsapp_link: formValue.whatsapp_link?.trim() || undefined,
          viber_link: formValue.viber_link?.trim() || undefined,
        }),
      );

      const userId = created?.user?.id;
      if (!userId) {
        throw new Error('Missing user id in create response');
      }

      const pendingPhoto = this.photoFile();
      if (pendingPhoto) {
        try {
          await firstValueFrom(
            this.#photoUpload.uploadPhoto('user', userId, pendingPhoto),
          );
          this.clearPendingPhoto();
        } catch (uploadErr) {
          console.error('Client photo upload failed', uploadErr);
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'error',
            text: [
              this.#translate.instant('admin.users.create.photoUploadError'),
            ],
          });
          await this.#router.navigate([
            this.#cabinetPaths.staff,
            'users',
            userId,
          ]);
          return;
        }
      }

      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [this.#translate.instant('admin.users.create.success')],
      });

      await this.#router.navigate([
        this.#cabinetPaths.staff,
        'users',
        userId,
      ]);
    } catch (error) {
      console.error('Failed to create client:', error);
      const isDuplicatePhone =
        error instanceof HttpErrorResponse && error.status === 409;
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: isDuplicatePhone ? 'warning' : 'error',
        text: [
          this.#translate.instant(
            isDuplicatePhone
              ? 'admin.users.create.phoneExists'
              : 'admin.users.create.error',
          ),
        ],
      });
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
